import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { fetchCountries, getAllCountries } from '../services/api';

const QuizContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100vh;
  background-color: #f5f5f5;
  padding: 20px;
`;

const QuizHeader = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  max-width: 800px;
  margin-bottom: 20px;
`;

const InfoItem = styled.div`
  background-color: white;
  padding: 10px 15px;
  border-radius: 5px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  font-size: 1.1rem;
  margin: 5px;
  flex: 1;
  min-width: 120px;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 8px 10px;
  }
`;

const FlagContainer = styled.div`
  background-color: white;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 800px;
  margin-bottom: 20px;
  display: flex;
  justify-content: center;
`;

const FlagImage = styled.img`
  max-width: 100%;
  max-height: 300px;
  object-fit: contain;
`;

const AnswerContainer = styled.div`
  width: 100%;
  max-width: 800px;
  position: relative;
`;

const AnswerInput = styled.input`
  width: 100%;
  padding: 12px;
  font-size: 1.1rem;
  border: 2px solid #ddd;
  border-radius: 5px;
  margin-bottom: 15px;
`;

const AutocompleteContainer = styled.div`
  position: absolute;
  width: 100%;
  max-height: 200px;
  overflow-y: auto;
  background-color: white;
  border: 1px solid #ddd;
  border-radius: 5px 5px 0 0;
  z-index: 10;
  box-shadow: 0 -4px 8px rgba(0, 0, 0, 0.1);
  display: ${props => props.show ? 'block' : 'none'};
  bottom: 100%;
  margin-bottom: 1px;
`;

const AutocompleteItem = styled.div`
  padding: 10px 15px;
  cursor: pointer;
  background-color: ${props => props.active ? '#e0e0e0' : 'white'};
  &:hover {
    background-color: #f5f5f5;
  }
  &:not(:last-child) {
    border-bottom: 1px solid #eee;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
`;

const SubmitButton = styled.button`
  background-color: #4caf50;
  color: white;
  padding: 12px 20px;
  font-size: 1.1rem;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  flex: ${props => props.fullWidth ? '1' : '3'};
  transition: background-color 0.3s;

  &:hover {
    background-color: #45a049;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const SkipButton = styled.button`
  background-color: #f0ad4e;
  color: white;
  padding: 12px 20px;
  font-size: 1.1rem;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  flex: 1;
  transition: background-color 0.3s;

  &:hover {
    background-color: #ec971f;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const GiveUpButton = styled.button`
  background-color: #d9534f;
  color: white;
  padding: 12px 20px;
  font-size: 1.1rem;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  width: 100%;
  margin-top: 10px;
  transition: background-color 0.3s;

  &:hover {
    background-color: #c9302c;
  }
`;

const ConfirmationOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
`;

const ConfirmationDialog = styled.div`
  background-color: white;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 400px;
  text-align: center;
`;

const ConfirmationTitle = styled.h3`
  margin-bottom: 20px;
  color: #333;
`;

const ConfirmationButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 20px;
`;

const ConfirmButton = styled.button`
  background-color: #d9534f;
  color: white;
  padding: 10px 15px;
  font-size: 1rem;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s;

  &:hover {
    background-color: #c9302c;
  }
`;

const CancelButton = styled.button`
  background-color: #5bc0de;
  color: white;
  padding: 10px 15px;
  font-size: 1rem;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s;

  &:hover {
    background-color: #46b8da;
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  max-width: 800px;
  height: 10px;
  background-color: #ddd;
  border-radius: 5px;
  margin-bottom: 20px;
  overflow: hidden;
`;

const Progress = styled.div`
  height: 100%;
  background-color: #4caf50;
  width: ${props => props.percentage}%;
  transition: width 0.3s ease;
`;

const FeedbackMessage = styled.div`
  width: 100%;
  max-width: 800px;
  padding: 15px;
  margin-top: 15px;
  border-radius: 5px;
  text-align: center;
  font-size: 1.2rem;
  background-color: ${props => (props.isCorrect ? '#dff0d8' : '#f2dede')};
  color: ${props => (props.isCorrect ? '#3c763d' : '#a94442')};
  display: ${props => (props.visible ? 'block' : 'none')};
`;

const LivesContainer = styled.div`
  display: flex;
  gap: 5px;
`;

const Heart = styled.span`
  color: ${props => (props.active ? 'red' : '#ddd')};
  font-size: 1.5rem;
`;

const Quiz = () => {
  const navigate = useNavigate();
  const [countries, setCountries] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answer, setAnswer] = useState('');
  const [score, setScore] = useState(0);
  const [feedback, setFeedback] = useState({ visible: false, isCorrect: false, message: '' });
  const [timeLeft, setTimeLeft] = useState(30); // Default time limit
  const [elapsedTime, setElapsedTime] = useState(0); // Track elapsed time for marathon mode
  const [lives, setLives] = useState(3);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({
    gameMode: 'standard',
    numberOfQuestions: 10,
    timeLimit: 30,
    lives: 3,
    difficulty: 'medium',
    marathonTimeLimit: 300,
    infiniteTime: false,
    useAllCountries: false
  });
  const [autocompleteOptions, setAutocompleteOptions] = useState([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [allCountryNames, setAllCountryNames] = useState([]);
  const [skippedCountries, setSkippedCountries] = useState([]);
  const [completedCountries, setCompletedCountries] = useState([]);
  const [activeAutocompleteIndex, setActiveAutocompleteIndex] = useState(-1);
  const [showGiveUpConfirm, setShowGiveUpConfirm] = useState(false);
  const timerRef = useRef(null);
  const inputRef = useRef(null);

  // Load settings and fetch countries
  useEffect(() => {
    const loadSettings = async () => {
      const savedSettings = localStorage.getItem('quizSettings');
      if (savedSettings) {
        const parsedSettings = JSON.parse(savedSettings);
        setSettings(parsedSettings);
        setLives(parsedSettings.lives);

        // Fetch all country names for autocomplete
        try {
          const allCountries = await getAllCountries();
          setAllCountryNames(allCountries.map(country => country.name));
        } catch (error) {
          console.error('Error fetching all country names:', error);
        }

        // Fetch countries based on game mode
        if (parsedSettings.gameMode === 'standard') {
          fetchStandardModeCountries(parsedSettings.numberOfQuestions);
        } else {
          // Marathon mode
          if (parsedSettings.useAllCountries) {
            fetchAllCountries();
          } else {
            fetchStandardModeCountries(parsedSettings.numberOfQuestions);
          }
        }
      }
    };

    loadSettings();
  }, []);

  const fetchStandardModeCountries = async (count) => {
    try {
      const data = await fetchCountries(count);
      setCountries(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching countries:', error);
      // For development, use mock data if API fails
      const mockData = [
        { name: 'United States', code: 'US', flag: 'https://flagcdn.com/us.svg' },
        { name: 'United Kingdom', code: 'GB', flag: 'https://flagcdn.com/gb.svg' },
        { name: 'France', code: 'FR', flag: 'https://flagcdn.com/fr.svg' },
        { name: 'Germany', code: 'DE', flag: 'https://flagcdn.com/de.svg' },
        { name: 'Japan', code: 'JP', flag: 'https://flagcdn.com/jp.svg' },
        { name: 'Brazil', code: 'BR', flag: 'https://flagcdn.com/br.svg' },
        { name: 'Canada', code: 'CA', flag: 'https://flagcdn.com/ca.svg' },
        { name: 'Australia', code: 'AU', flag: 'https://flagcdn.com/au.svg' },
        { name: 'India', code: 'IN', flag: 'https://flagcdn.com/in.svg' },
        { name: 'China', code: 'CN', flag: 'https://flagcdn.com/cn.svg' }
      ];
      setCountries(mockData);
      setLoading(false);
    }
  };

  const fetchAllCountries = async () => {
    try {
      const data = await getAllCountries();
      // Shuffle the countries
      const shuffled = [...data].sort(() => 0.5 - Math.random());
      setCountries(shuffled);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching all countries:', error);
      // Use mock data if API fails
      const mockData = [
        { name: 'United States', code: 'US', flag: 'https://flagcdn.com/us.svg' },
        { name: 'United Kingdom', code: 'GB', flag: 'https://flagcdn.com/gb.svg' },
        { name: 'France', code: 'FR', flag: 'https://flagcdn.com/fr.svg' },
        { name: 'Germany', code: 'DE', flag: 'https://flagcdn.com/de.svg' },
        { name: 'Japan', code: 'JP', flag: 'https://flagcdn.com/jp.svg' },
        { name: 'Brazil', code: 'BR', flag: 'https://flagcdn.com/br.svg' },
        { name: 'Canada', code: 'CA', flag: 'https://flagcdn.com/ca.svg' },
        { name: 'Australia', code: 'AU', flag: 'https://flagcdn.com/au.svg' },
        { name: 'India', code: 'IN', flag: 'https://flagcdn.com/in.svg' },
        { name: 'China', code: 'CN', flag: 'https://flagcdn.com/cn.svg' }
      ];
      setCountries(mockData);
      setLoading(false);
    }
  };

  // Declare functions first to avoid circular dependencies
  let startTimer;
  let handleTimeout;
  let nextQuestion;
  let endQuiz;

  // Define endQuiz
  endQuiz = useCallback(() => {
    clearInterval(timerRef.current);
    
    // Store results
    const results = {
      score,
      total: settings.gameMode === 'marathon' ? completedCountries.length : countries.length,
      lives: lives,
      settings,
      completedCountries: settings.gameMode === 'marathon' ? completedCountries : [],
      timeRemaining: timeLeft,
      elapsedTime: settings.gameMode === 'marathon' ? elapsedTime : 0 // Save elapsed time for marathon mode
    };
    
    localStorage.setItem('quizResults', JSON.stringify(results));
    navigate('/results');
  }, [completedCountries, countries.length, elapsedTime, lives, navigate, score, settings, timeLeft]);

  // Define handleTimeout
  handleTimeout = useCallback(() => {
    if (settings.gameMode === 'standard') {
      // Standard mode - lose a life when time's up
      setFeedback({
        visible: true,
        isCorrect: false,
        message: `Time's up! The correct answer was ${countries[currentIndex].name}.`
      });
      
      setLives(prevLives => prevLives - 1);
      
      setTimeout(() => {
        if (lives <= 1) {
          endQuiz();
        } else {
          nextQuestion();
        }
      }, 2000);
    } else {
      // Marathon mode - end quiz when global timer ends
      endQuiz();
    }
  }, [countries, currentIndex, endQuiz, lives, nextQuestion, settings.gameMode]);

  // Define startTimer
  startTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    
    timerRef.current = setInterval(() => {
      // For marathon mode, increment elapsed time
      if (settings.gameMode === 'marathon') {
        setElapsedTime(prevTime => prevTime + 1);
      }
      
      setTimeLeft(prevTime => {
        if (prevTime <= 1 || prevTime === Infinity) {
          if (prevTime <= 1) {
            clearInterval(timerRef.current);
            handleTimeout();
            return 0;
          }
          return Infinity; // Keep infinite time as is
        }
        return prevTime - 1;
      });
    }, 1000);
  }, [handleTimeout, settings.gameMode]);

  // Define nextQuestion
  nextQuestion = useCallback(() => {
    setFeedback({ visible: false, isCorrect: false, message: '' });
    setAnswer('');
    setShowAutocomplete(false);
    
    if (currentIndex < countries.length - 1) {
      setCurrentIndex(prevIndex => prevIndex + 1);
      
      if (settings.gameMode === 'standard') {
        // Reset timer for each question in standard mode
        setTimeLeft(settings.timeLimit);
        startTimer();
      }
      
      // Focus on input field for next question
      // Using setTimeout to ensure focus happens after state updates and render
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 50);
    } else {
      // In marathon mode, if we've gone through all countries but still have skipped ones,
      // we can cycle back through the skipped countries
      if (settings.gameMode === 'marathon' && skippedCountries.length > 0) {
        setCountries(skippedCountries);
        setSkippedCountries([]);
        setCurrentIndex(0);
        
        // Focus on input field after cycling back to skipped countries
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }, 50);
      } else {
        endQuiz();
      }
    }
  }, [countries.length, currentIndex, endQuiz, inputRef, settings.gameMode, settings.timeLimit, skippedCountries, startTimer]);

  // startTimer is now defined earlier

  // Set up timer
  useEffect(() => {
    if (!loading && countries.length > 0) {
      if (settings.gameMode === 'standard') {
        // Standard mode - timer per question
        setTimeLeft(settings.timeLimit);
      } else {
        // Marathon mode - timer for entire quiz or infinite
        if (!settings.infiniteTime) {
          setTimeLeft(settings.marathonTimeLimit);
        } else {
          setTimeLeft(Infinity); // Infinite time
        }
      }
      startTimer();
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [loading, countries, settings.gameMode, settings.infiniteTime, settings.marathonTimeLimit, settings.timeLimit, startTimer]);

  // Handle answer submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (settings.gameMode === 'standard') {
      clearInterval(timerRef.current);
    }
    
    const currentCountry = countries[currentIndex];
    const isCorrect = answer.toLowerCase() === currentCountry.name.toLowerCase();
    
    if (isCorrect) {
      setScore(prevScore => prevScore + 1);
      setFeedback({
        visible: true,
        isCorrect: true,
        message: 'Correct!'
      });

      if (settings.gameMode === 'marathon') {
        // Add to completed countries in marathon mode
        setCompletedCountries(prev => [...prev, currentCountry]);
      }
    } else {
      if (settings.gameMode === 'standard') {
        // Only lose lives in standard mode
        setLives(prevLives => prevLives - 1);
      }
      
      setFeedback({
        visible: true,
        isCorrect: false,
        message: `Incorrect! The correct answer was ${currentCountry.name}.`
      });
    }
    
    setTimeout(() => {
      if (settings.gameMode === 'standard' && !isCorrect && lives <= 1) {
        endQuiz();
      } else {
        nextQuestion();
      }
    }, 2000);
  };

  // Skip current question (marathon mode only)
  const handleSkip = () => {
    const currentCountry = countries[currentIndex];
    setSkippedCountries(prev => [...prev, currentCountry]);
    nextQuestion();
  };

  // Show give up confirmation dialog
  const showGiveUpConfirmation = () => {
    setShowGiveUpConfirm(true);
  };
  
  // Handle give up confirmation
  const handleGiveUpConfirm = () => {
    setShowGiveUpConfirm(false);
    endQuiz();
  };
  
  // Handle give up cancellation
  const handleGiveUpCancel = () => {
    setShowGiveUpConfirm(false);
  };

  // The nextQuestion function is now defined earlier using useCallback

  // Handle input changes for autocomplete and auto-submit in marathon mode
  const handleInputChange = (e) => {
    const value = e.target.value;
    setAnswer(value);
    
    // Reset active autocomplete index when input changes
    setActiveAutocompleteIndex(-1);
    
    // Auto-submit in marathon mode if the answer is correct
    if (settings.gameMode === 'marathon' && !feedback.visible) {
      const currentCountry = countries[currentIndex];
      if (value.toLowerCase() === currentCountry.name.toLowerCase()) {
        // Automatically submit the correct answer
        handleCorrectAnswer(currentCountry);
        return;
      }
    }
    
    // Show autocomplete suggestions after typing 3 characters
    if (value.length >= 3) {
      const filteredOptions = allCountryNames.filter(name => 
        name.toLowerCase().includes(value.toLowerCase())
      ).slice(0, 5); // Limit to 5 suggestions
      
      setAutocompleteOptions(filteredOptions);
      setShowAutocomplete(filteredOptions.length > 0);
    } else {
      setShowAutocomplete(false);
    }
  };
  
  // Handle keyboard navigation for autocomplete
  const handleKeyDown = (e) => {
    // Only process if autocomplete is visible
    if (!showAutocomplete || autocompleteOptions.length === 0) {
      return;
    }
    
    // Tab key - cycle through options
    if (e.key === 'Tab') {
      e.preventDefault(); // Prevent default tab behavior
      
      setActiveAutocompleteIndex(prevIndex => {
        // Cycle to the next option or back to the first if at the end
        const nextIndex = prevIndex + 1 >= autocompleteOptions.length ? 0 : prevIndex + 1;
        return nextIndex;
      });
    }
    
    // Enter key - select the active option
    else if (e.key === 'Enter' && activeAutocompleteIndex >= 0) {
      e.preventDefault(); // Prevent form submission
      selectAutocompleteOption(autocompleteOptions[activeAutocompleteIndex]);
    }
    
    // Escape key - close autocomplete
    else if (e.key === 'Escape') {
      setShowAutocomplete(false);
      setActiveAutocompleteIndex(-1);
    }
    
    // Arrow down - move to next option
    else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveAutocompleteIndex(prevIndex => {
        const nextIndex = prevIndex + 1 >= autocompleteOptions.length ? 0 : prevIndex + 1;
        return nextIndex;
      });
    }
    
    // Arrow up - move to previous option
    else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveAutocompleteIndex(prevIndex => {
        const nextIndex = prevIndex - 1 < 0 ? autocompleteOptions.length - 1 : prevIndex - 1;
        return nextIndex;
      });
    }
  };
  
  // Handle correct answer (for auto-submit in marathon mode)
  const handleCorrectAnswer = (currentCountry) => {
    setScore(prevScore => prevScore + 1);
    setFeedback({
      visible: true,
      isCorrect: true,
      message: 'Correct!'
    });

    // Add to completed countries in marathon mode
    setCompletedCountries(prev => [...prev, currentCountry]);
    
    // Move to next question after a short delay
    setTimeout(() => {
      nextQuestion();
    }, 1000); // Shorter delay for better flow in marathon mode
  };

  // Select an autocomplete option
  const selectAutocompleteOption = (option) => {
    setAnswer(option);
    setShowAutocomplete(false);
    
    // Focus back on input after selection
    if (inputRef.current) {
      inputRef.current.focus();
    }
    
    // Auto-submit in marathon mode if the selected option is correct
    if (settings.gameMode === 'marathon' && !feedback.visible) {
      const currentCountry = countries[currentIndex];
      if (option.toLowerCase() === currentCountry.name.toLowerCase()) {
        // Automatically submit the correct answer
        handleCorrectAnswer(currentCountry);
      }
    }
  };

  // The endQuiz function is now defined earlier using useCallback

  // Calculate progress percentage
  const progressPercentage = settings.gameMode === 'standard' 
    ? (currentIndex / countries.length) * 100
    : (completedCountries.length / (settings.useAllCountries ? countries.length : settings.numberOfQuestions)) * 100;

  if (loading) {
    return <QuizContainer>Loading quiz...</QuizContainer>;
  }

  return (
    <QuizContainer>
      {/* Give Up Confirmation Dialog */}
      {showGiveUpConfirm && (
        <ConfirmationOverlay>
          <ConfirmationDialog>
            <ConfirmationTitle>Are you sure you want to give up?</ConfirmationTitle>
            <p>Your current progress will be lost and you'll see your final score.</p>
            <ConfirmationButtons>
              <CancelButton onClick={handleGiveUpCancel}>Cancel</CancelButton>
              <ConfirmButton onClick={handleGiveUpConfirm}>Yes, Give Up</ConfirmButton>
            </ConfirmationButtons>
          </ConfirmationDialog>
        </ConfirmationOverlay>
      )}
      <QuizHeader>
        {settings.gameMode === 'standard' ? (
          // Standard mode header
          <>
            <InfoItem>
              Question: {currentIndex + 1}/{countries.length}
            </InfoItem>
            <InfoItem>
              Time Left: {timeLeft}s
            </InfoItem>
            <InfoItem>
              Score: {score}
            </InfoItem>
            <InfoItem>
              <LivesContainer>
                Lives: {Array.from({ length: settings.lives }).map((_, i) => (
                  <Heart key={i} active={i < lives}>❤</Heart>
                ))}
              </LivesContainer>
            </InfoItem>
          </>
        ) : (
          // Marathon mode header
          <>
            <InfoItem>
              Flags Identified: {completedCountries.length}/{settings.useAllCountries ? countries.length : settings.numberOfQuestions}
            </InfoItem>
            {/* Only show time left if not infinite time */}
            {!settings.infiniteTime && (
              <InfoItem>
                Time Left: {timeLeft}s
              </InfoItem>
            )}
            <InfoItem>
              Current Flag: {currentIndex + 1}/{countries.length}
            </InfoItem>
          </>
        )}
      </QuizHeader>

      <ProgressBar>
        <Progress percentage={progressPercentage} />
      </ProgressBar>

      <FlagContainer>
        <FlagImage src={countries[currentIndex].flag} alt="Country Flag" />
      </FlagContainer>

      <AnswerContainer>
        <form onSubmit={handleSubmit}>
          <AnswerInput
            type="text"
            placeholder="Enter country name..."
            value={answer}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            disabled={feedback.visible}
            autoFocus
            ref={inputRef}
          />
          {showAutocomplete && (
            <AutocompleteContainer show={showAutocomplete}>
              {autocompleteOptions.map((option, index) => (
                <AutocompleteItem 
                  key={index} 
                  active={index === activeAutocompleteIndex}
                  onClick={() => selectAutocompleteOption(option)}
                >
                  {option}
                </AutocompleteItem>
              ))}
            </AutocompleteContainer>
          )}
          
          {settings.gameMode === 'standard' ? (
            // Standard mode buttons
            <SubmitButton 
              type="submit" 
              disabled={!answer.trim() || feedback.visible}
              fullWidth
            >
              Submit Answer
            </SubmitButton>
          ) : (
            // Marathon mode buttons - no submit button, just skip and give up
            <>
              <ButtonContainer>
                <SkipButton 
                  type="button" 
                  onClick={handleSkip}
                  disabled={feedback.visible}
                  fullWidth
                >
                  Skip This Flag
                </SkipButton>
              </ButtonContainer>
              
              <GiveUpButton 
                type="button" 
                onClick={showGiveUpConfirmation}
                disabled={feedback.visible}
              >
                Give Up
              </GiveUpButton>
            </>
          )}
        </form>
      </AnswerContainer>

      <FeedbackMessage visible={feedback.visible} isCorrect={feedback.isCorrect}>
        {feedback.message}
      </FeedbackMessage>
    </QuizContainer>
  );
};

export default Quiz;
